import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Users, DollarSign, Megaphone, Settings as SettingsIcon, Camera, Shield, Save, Check, X, Search, Star } from "lucide-react";
import {
  adminTrashDashboard, adminTrashMembers, adminTrashMemberEdit,
  adminTrashScans, adminTrashScanAction,
  adminTrashPayouts, adminTrashPayoutAction,
  adminTrashSetAnnouncement, adminTrashSettings, adminTrashSettingsEdit,
  cents, humanErr,
} from "@/lib/trashApi";

const SUBS = [
  { id: "dashboard", label: "Dashboard", icon: <Shield className="w-3 h-3" /> },
  { id: "members",   label: "Members",   icon: <Users className="w-3 h-3" /> },
  { id: "scans",     label: "Cleanup Review", icon: <Camera className="w-3 h-3" /> },
  { id: "payouts",   label: "Payouts",   icon: <DollarSign className="w-3 h-3" /> },
  { id: "banner",    label: "Home Banner", icon: <Megaphone className="w-3 h-3" /> },
  { id: "settings",  label: "Settings",  icon: <SettingsIcon className="w-3 h-3" /> },
];

export default function TrashAdminTab() {
  const [sub, setSub] = useState("dashboard");
  return (
    <div className="space-y-4" data-testid="trash-admin">
      <div className="flex gap-1 flex-wrap">
        {SUBS.map((s) => (
          <button key={s.id} data-testid={`trash-admin-sub-${s.id}`} onClick={() => setSub(s.id)}
            className={"mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full inline-flex items-center gap-1 " +
              (sub === s.id ? "bg-[#39FF14] text-black" : "border border-white/20 bg-white/5 text-white/70")}>
            {s.icon}{s.label}
          </button>
        ))}
      </div>
      {sub === "dashboard" && <Dashboard />}
      {sub === "members" && <Members />}
      {sub === "scans" && <Scans />}
      {sub === "payouts" && <Payouts />}
      {sub === "banner" && <BannerCms />}
      {sub === "settings" && <SettingsPanel />}
    </div>
  );
}

function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { adminTrashDashboard().then(setD).catch(() => {}); }, []);
  if (!d) return <div className="py-16 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  const t = d.totals;
  return (
    <div className="space-y-4" data-testid="trash-admin-dashboard">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Tile label="Members" value={t.members} />
        <Tile label="Elite" value={t.elite_members} accent="#FFD700" />
        <Tile label="Cleanups" value={t.cleanups_total} accent="#39FF14" />
        <Tile label="Today" value={t.cleanups_today} accent="#00F0FF" />
        <Tile label="Verified" value={t.verified_total} accent="#00F0FF" />
        <Tile label="Payouts pending" value={t.payouts_pending} accent="#FFD700" />
        <Tile label="Payouts paid" value={t.payouts_paid} accent="#39FF14" />
      </div>
      <div>
        <div className="font-black text-sm uppercase tracking-widest text-white/80 mb-2">Recent activity</div>
        {d.recent.length === 0 ? <div className="text-white/50 text-sm">No cleanups yet.</div> :
          <ul className="space-y-1">
            {d.recent.slice(0, 10).map((r) => (
              <li key={r.id} className="rounded-xl border border-white/10 bg-black/40 p-2 text-xs flex items-center gap-2">
                <div className="flex-1">
                  <b>{r.user_name}</b> cleaned <span className="text-[#39FF14]">{r.detected_type}</span>
                  {r.verified && <span className="ml-1 text-[9px] mono uppercase tracking-widest text-[#39FF14]">Verified</span>}
                </div>
                <div className="text-white/50 text-[10px]">{r.created_at?.slice(0, 16).replace("T", " ")}</div>
              </li>
            ))}
          </ul>}
      </div>
    </div>
  );
}

function Members() {
  const [q, setQ] = useState("");
  const [list, setList] = useState(null);
  const [inputs, setInputs] = useState({});   // per-uid pending edit inputs
  const load = () => adminTrashMembers({ q, limit: 100 }).then((r) => setList(r.members)).catch(() => {});
  useEffect(() => { const t = setTimeout(load, q ? 300 : 0); return () => clearTimeout(t); }, [q]);

  const patch = async (uid, body) => {
    try {
      await adminTrashMemberEdit(uid, body);
      toast.success("Updated");
      load(); setInputs({ ...inputs, [uid]: {} });
    } catch (e) { toast.error(humanErr(e, "Update failed")); }
  };

  return (
    <div className="space-y-3" data-testid="trash-admin-members">
      <div className="relative">
        <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
        <input data-testid="ta-members-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search email/name…"
          className="w-full bg-white/5 border border-white/10 rounded-full pl-10 pr-4 py-2 text-sm" />
      </div>
      {!list ? <div className="py-12 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> :
        <ul className="space-y-1">
          {list.map((m) => {
            const i = inputs[m.id] || {};
            return (
              <li key={m.id} data-testid={`ta-member-${m.id}`}
                className="rounded-2xl border border-white/10 bg-black/40 p-3 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="w-8 h-8 rounded-lg shrink-0" style={{ background: m.avatar_color }} />
                  <div className="min-w-0 flex-1">
                    <div className="font-black text-sm truncate">{m.name} {m.is_verified_member && <Check className="w-3 h-3 inline text-[#39FF14]" />}{m.is_elite && <Star className="w-3 h-3 inline text-[#FFD700] ml-1" />}</div>
                    <div className="text-[10px] text-white/50 truncate">{m.email} · {m.role || "user"} {m.is_banned && <span className="text-[#F87171]">· BANNED</span>}{m.is_suspended && <span className="text-[#F87171]">· SUSPENDED</span>}</div>
                  </div>
                  <div className="text-right text-[10px] mono uppercase tracking-widest text-white/50">
                    <div>{m.xp} XP · L{Math.floor(m.xp / 250) + 1}</div>
                    <div>{m.coins} coins · {cents(m.cash_cents)}</div>
                    <div>{m.cleanups_total} cleanups · {m.verified_count} verified</div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1">
                  <NumInput placeholder="+/- coins" val={i.coins_delta ?? ""}
                            testId={`ta-coins-${m.id}`}
                            onChange={(v) => setInputs({ ...inputs, [m.id]: { ...i, coins_delta: v } })} />
                  <NumInput placeholder="+/- cents" val={i.cash_cents_delta ?? ""}
                            testId={`ta-cash-${m.id}`}
                            onChange={(v) => setInputs({ ...inputs, [m.id]: { ...i, cash_cents_delta: v } })} />
                  <NumInput placeholder="+/- XP" val={i.xp_delta ?? ""}
                            testId={`ta-xp-${m.id}`}
                            onChange={(v) => setInputs({ ...inputs, [m.id]: { ...i, xp_delta: v } })} />
                  <button data-testid={`ta-apply-${m.id}`} onClick={() => patch(m.id, i)} disabled={Object.keys(i).length === 0}
                    className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-[#39FF14] text-black font-black disabled:opacity-40">Apply</button>
                  <button data-testid={`ta-verify-${m.id}`} onClick={() => patch(m.id, { is_verified_member: !m.is_verified_member })}
                    className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border border-white/20 bg-white/5">{m.is_verified_member ? "Un-verify" : "Verify"}</button>
                  <button data-testid={`ta-suspend-${m.id}`} onClick={() => patch(m.id, { is_suspended: !m.is_suspended })}
                    className={"mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full " + (m.is_suspended ? "bg-[#39FF14] text-black" : "border border-[#F87171]/40 bg-[#F87171]/10 text-[#F87171]")}>
                    {m.is_suspended ? "Unsuspend" : "Suspend"}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>}
    </div>
  );
}

function Scans() {
  const [rows, setRows] = useState(null);
  const [flagged, setFlagged] = useState(false);
  const load = () => adminTrashScans({ flagged_only: flagged, limit: 100 }).then((r) => setRows(r.scans)).catch(() => {});
  useEffect(() => { load(); }, [flagged]);

  const act = async (sid, action) => {
    try { await adminTrashScanAction(sid, action); toast.success(action); load(); }
    catch (e) { toast.error(humanErr(e, "Action failed")); }
  };

  return (
    <div className="space-y-3" data-testid="trash-admin-scans">
      <label className="inline-flex items-center gap-2 text-xs text-white/70">
        <input type="checkbox" checked={flagged} onChange={(e) => setFlagged(e.target.checked)} data-testid="ta-flagged-only" />
        Show flagged only
      </label>
      {!rows ? <div className="py-12 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> :
        rows.length === 0 ? <div className="text-white/50 text-sm">No scans to review.</div> :
        <ul className="space-y-1">
          {rows.map((r) => (
            <li key={r.id} data-testid={`ta-scan-${r.id}`}
              className="rounded-2xl border border-white/10 bg-black/40 p-3 flex flex-wrap items-center gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-black text-sm capitalize">{(r.detected_type || "unknown").replace(/_/g, " ")}</div>
                <div className="text-[10px] text-white/50 truncate">by {r.user_name} · confidence {Math.round((r.confidence || 0) * 100)}% · {r.verified ? "GPS verified" : "unverified"}{r.gps_lat != null && ` (${r.gps_lat.toFixed?.(4)}, ${r.gps_lon.toFixed?.(4)})`}</div>
                {r.ai_reason && <div className="text-[10px] text-white/40 mt-0.5">{r.ai_reason}</div>}
              </div>
              <div className="flex gap-1">
                <button data-testid={`ta-scan-approve-${r.id}`} onClick={() => act(r.id, "approve")}
                  className="mono text-[10px] uppercase tracking-widest px-3 py-1 rounded-full bg-[#39FF14] text-black font-black"><Check className="w-3 h-3 inline" /></button>
                <button data-testid={`ta-scan-reject-${r.id}`} onClick={() => act(r.id, "reject")}
                  className="mono text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border border-[#F87171]/40 bg-[#F87171]/10 text-[#F87171]"><X className="w-3 h-3 inline" /></button>
              </div>
            </li>
          ))}
        </ul>}
    </div>
  );
}

function Payouts() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState(null);
  const load = () => adminTrashPayouts(status).then((r) => setRows(r.payouts)).catch(() => {});
  useEffect(() => { load(); }, [status]);

  const act = async (rid, action) => {
    if (!window.confirm(`${action} this cash-out?`)) return;
    try { await adminTrashPayoutAction(rid, action); toast.success(action); load(); }
    catch (e) { toast.error(humanErr(e, "Action failed")); }
  };

  return (
    <div className="space-y-3" data-testid="trash-admin-payouts">
      <div className="flex gap-1 bg-white/5 border border-white/10 rounded-full p-1 w-fit">
        {["pending","paid","rejected"].map((s) => (
          <button key={s} data-testid={`ta-payout-tab-${s}`} onClick={() => setStatus(s)}
            className={"px-3 py-1 text-xs uppercase tracking-widest font-black rounded-full " + (status === s ? "bg-[#39FF14] text-black" : "text-white/60")}>{s}</button>
        ))}
      </div>
      {!rows ? <div className="py-12 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> :
        rows.length === 0 ? <div className="text-white/50 text-sm">No {status} cash-outs.</div> :
        <ul className="space-y-1">
          {rows.map((r) => (
            <li key={r.id} data-testid={`ta-payout-${r.id}`}
              className="rounded-2xl border border-white/10 bg-black/40 p-3 flex items-center gap-2 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="font-black text-sm">{cents(r.cash_cents)} to <span className="text-[#39FF14]">{r.method}</span></div>
                <div className="text-[10px] text-white/50 truncate">by {r.name || r.user_id} ({r.email}) · handle: {r.handle} · fee {cents(r.fee_cents)}</div>
                <div className="text-[10px] text-white/40">{r.created_at?.slice(0, 16).replace("T", " ")}{r.processed_at && ` · ${r.status} ${r.processed_at?.slice(0, 16).replace("T", " ")}`}</div>
              </div>
              {status === "pending" && <div className="flex gap-1">
                <button data-testid={`ta-payout-approve-${r.id}`} onClick={() => act(r.id, "approve")}
                  className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-[#39FF14] text-black font-black">Approve</button>
                <button data-testid={`ta-payout-reject-${r.id}`} onClick={() => act(r.id, "reject")}
                  className="mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border border-[#F87171]/40 bg-[#F87171]/10 text-[#F87171]">Reject + refund</button>
              </div>}
            </li>
          ))}
        </ul>}
    </div>
  );
}

function BannerCms() {
  const [text, setText] = useState("");
  const [color, setColor] = useState("green");
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await adminTrashSetAnnouncement({ text: text.trim(), color, active });
      toast.success("Broadcast updated");
    } catch (e) { toast.error(humanErr(e, "Save failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="trash-admin-banner">
      <div className="rounded-3xl border-2 border-[#39FF14]/40 bg-black/40 p-5 space-y-3">
        <div className="font-black text-lg">Home banner broadcast</div>
        <textarea data-testid="ta-banner-text" value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={280}
          placeholder="Message shown at the top of Trash Hunter home (≤280 chars)"
          className="w-full bg-white/5 border border-white/10 rounded-2xl px-3 py-2 text-sm" />
        <div className="flex gap-2 flex-wrap items-center">
          <div className="text-[10px] mono uppercase tracking-widest text-white/50">Color:</div>
          {["green","cyan","magenta","gold","red"].map((c) => (
            <button key={c} data-testid={`ta-banner-color-${c}`} onClick={() => setColor(c)}
              className={"w-6 h-6 rounded-full border-2 " + (color === c ? "border-white" : "border-transparent")}
              style={{ background: c === "green" ? "#39FF14" : c === "cyan" ? "#00F0FF" : c === "magenta" ? "#FF00E5" : c === "gold" ? "#FFD700" : "#F87171" }} />
          ))}
          <label className="inline-flex items-center gap-1 text-xs text-white/70 ml-auto">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="ta-banner-active" /> Active
          </label>
        </div>
        <button data-testid="ta-banner-save" onClick={submit} disabled={busy || !text.trim()}
          className="w-full bg-[#39FF14] text-black font-black text-sm uppercase tracking-widest px-6 py-3 rounded-full disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : <><Save className="w-4 h-4 inline mr-1" /> Save + broadcast</>}
        </button>
      </div>
    </div>
  );
}

function SettingsPanel() {
  const [s, setS] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { adminTrashSettings().then((r) => { setS(r.settings); setForm(r.settings); }).catch(() => {}); }, []);
  if (!s) return <div className="py-12 text-center text-white/40"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  const save = async () => {
    setBusy(true);
    try {
      const changed = {};
      for (const k of ["xp_per_scan","coins_per_scan","cash_per_scan_cents","cashout_min_cents","cashout_fee_cents","daily_cash_cap_cents","verify_min","sub_price_cents","cooldown_radius_m","cooldown_seconds"]) {
        if (form[k] !== s[k]) changed[k] = Number(form[k]);
      }
      if (Object.keys(changed).length === 0) { toast.info("No changes"); return; }
      const r = await adminTrashSettingsEdit(changed);
      setS(r.settings); setForm(r.settings);
      toast.success("Settings saved");
    } catch (e) { toast.error(humanErr(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="trash-admin-settings">
      <div className="rounded-3xl border-2 border-[#39FF14]/40 bg-black/40 p-5 space-y-3">
        <div className="font-black text-lg">Trash Hunter settings</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <Setting label="XP / scan" k="xp_per_scan" form={form} setForm={setForm} />
          <Setting label="Coins / scan" k="coins_per_scan" form={form} setForm={setForm} />
          <Setting label="Cash / scan (¢)" k="cash_per_scan_cents" form={form} setForm={setForm} />
          <Setting label="Cash-out min (¢)" k="cashout_min_cents" form={form} setForm={setForm} />
          <Setting label="Cash-out fee (¢)" k="cashout_fee_cents" form={form} setForm={setForm} />
          <Setting label="Daily cap (¢)" k="daily_cash_cap_cents" form={form} setForm={setForm} />
          <Setting label="Verify min" k="verify_min" form={form} setForm={setForm} />
          <Setting label="Sub price (¢)" k="sub_price_cents" form={form} setForm={setForm} />
          <Setting label="Cooldown radius (m)" k="cooldown_radius_m" form={form} setForm={setForm} />
          <Setting label="Cooldown (sec)" k="cooldown_seconds" form={form} setForm={setForm} />
        </div>
        <button data-testid="ta-settings-save" onClick={save} disabled={busy}
          className="w-full bg-[#39FF14] text-black font-black text-sm uppercase tracking-widest px-6 py-3 rounded-full disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin inline" /> : <><Save className="w-4 h-4 inline mr-1" /> Save</>}
        </button>
      </div>
    </div>
  );
}

function Setting({ label, k, form, setForm }) {
  return (
    <label className="block">
      <div className="text-[10px] mono uppercase tracking-widest text-white/50 mb-1">{label}</div>
      <input type="number" data-testid={`ta-setting-${k}`} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        className="w-full bg-white/5 border border-white/10 rounded-2xl px-3 py-2 text-sm tabular-nums" />
    </label>
  );
}

function NumInput({ placeholder, val, onChange, testId }) {
  return (
    <input type="number" placeholder={placeholder} value={val} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      className="w-24 bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-xs tabular-nums"
      data-testid={testId} />
  );
}

function Tile({ label, value, accent = "#fff" }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-black/40 p-3">
      <div className="text-[9px] uppercase tracking-widest text-white/50">{label}</div>
      <div className="font-black text-2xl mt-0.5 tabular-nums" style={{ color: accent }}>{value?.toLocaleString?.() ?? value}</div>
    </div>
  );
}
